// @ts-nocheck
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Loader2 } from 'lucide-react';

export interface InviteCoachValue {
  primary_coach_name: string;
  primary_coach_email: string;
  primary_coach_phone: string;
  send_invite_now: boolean;
  personal_message: string;
}

interface Props {
  value: InviteCoachValue;
  onChange: (patch: Partial<InviteCoachValue>) => void;
  onBack: () => void;
  onSubmit: (opts?: { skip?: boolean }) => void;
  submitting: boolean;
}

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

export default function InviteCoachStep({ value, onChange, onBack, onSubmit, submitting }: Props) {
  const emailValid = !value.primary_coach_email || isValidEmail(value.primary_coach_email);
  const hasEmail = !!value.primary_coach_email && emailValid;

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="coach_name">Head coach name</Label>
        <Input
          id="coach_name"
          value={value.primary_coach_name}
          onChange={(e) => onChange({ primary_coach_name: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="coach_email">Head coach email</Label>
        <Input
          id="coach_email"
          type="email"
          value={value.primary_coach_email}
          onChange={(e) => onChange({ primary_coach_email: e.target.value })}
        />
        {!emailValid && (
          <p className="text-sm text-destructive">Please enter a valid email address</p>
        )}
        <p className="text-xs text-muted-foreground">
          If you enter an email, this person will be invited as the head coach. You can skip this and invite them later.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="coach_phone">Head coach phone</Label>
        <Input
          id="coach_phone"
          type="tel"
          value={value.primary_coach_phone}
          onChange={(e) => onChange({ primary_coach_phone: e.target.value })}
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <p className="text-sm font-medium">Send invite email now</p>
          <p className="text-xs text-muted-foreground">
            {hasEmail ? 'They will receive an email invite right after the team is created.' : 'Add an email above to enable this option.'}
          </p>
        </div>
        <Switch
          checked={value.send_invite_now && hasEmail}
          disabled={!hasEmail}
          onCheckedChange={(c) => onChange({ send_invite_now: c })}
        />
      </div>

      {value.send_invite_now && hasEmail && (
        <div className="space-y-2">
          <Label htmlFor="personal_message">Personal message (optional)</Label>
          <Textarea
            id="personal_message"
            rows={4}
            placeholder="Hey coach, I just set up our team on RecruitLook…"
            value={value.personal_message}
            onChange={(e) => onChange({ personal_message: e.target.value })}
          />
        </div>
      )}

      <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onBack} disabled={submitting}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="flex flex-col-reverse md:flex-row gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onSubmit({ skip: true })}
            disabled={submitting}
          >
            Skip — create without coach
          </Button>
          <Button type="button" onClick={() => onSubmit()} disabled={submitting || !emailValid}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create team
          </Button>
        </div>
      </div>
    </div>
  );
}
